import type { OnInit } from '@angular/core';
import {
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe } from '@ngx-translate/core';
import type { OnyxPaginated, OnyxTableNotFound } from '@onyx/angular';
import {
  OnyxButtonComponent,
  OnyxFormMode,
  OnyxModalService,
  OnyxTableComponent,
  OnyxToggleComponent,
} from '@onyx/angular';
import { catchError, EMPTY, Subject, switchMap, tap } from 'rxjs';
import type { Driver } from '../../../dashboard/drivers/common/interfaces/driver';
import { DriversService } from '../../../dashboard/drivers/common/services/drivers.service';
import type { Employee } from '../../../dashboard/management-panel/employees/common/interfaces/employee';
import { EmployeesService } from '../../../dashboard/management-panel/employees/common/services/employees.service';
import { SHORT_PAGINATION } from '../../constants/common/short-pagination';
import { getUnavailabilitiesColumns } from '../../constants/common/unavailabilities-columns';
import type { Unavailability } from '../../interfaces/common/unavailability';
import { StorageService } from '../../services/storage.service';
import type { UnavailabilityModalData } from './unavailability-modal/unavailability-modal.component';
import { UnavailabilityModalComponent } from './unavailability-modal/unavailability-modal.component';

export enum UnavailabilityType {
  EMPLOYEE = 'employee',
  DRIVER = 'driver',
}

@Component({
  selector: 'app-unavailabilities',
  imports: [
    OnyxButtonComponent,
    OnyxTableComponent,
    OnyxToggleComponent,
    TranslatePipe,
  ],
  templateUrl: './unavailabilities.component.html',
  styleUrl: './unavailabilities.component.scss',
})
export class UnavailabilitiesComponent implements OnInit {
  protected readonly employeesService = inject(EmployeesService);
  protected readonly driversService = inject(DriversService);
  protected readonly destroyRef = inject(DestroyRef);
  protected readonly storageService = inject(StorageService);
  protected readonly modalService = inject(OnyxModalService);
  protected readonly injector = inject(Injector);

  public readonly employee = input<Employee | null>(null);
  public readonly driver = input<Driver | null>(null);
  public readonly enabled = input(true);
  public readonly showAdd = input(true);

  protected readonly I18N = 'unavailabilities';
  protected readonly COLUMNS = getUnavailabilitiesColumns(
    this.showFile.bind(this),
  );
  protected readonly UNAVAILABILITIES_NOT_FOUND: OnyxTableNotFound = {
    icon: { name: 'vacation-filled', size: 32 },
    title: `${this.I18N}.empty.title`,
    text: `${this.I18N}.empty.text`,
  };

  protected readonly UnavailabilityType = UnavailabilityType;

  protected unavailabilities = signal<OnyxPaginated<Unavailability> | null>(
    null,
  );
  protected loading = signal(false);
  protected error = signal(false);
  protected showPastUnavailabilities = signal(false);
  protected pagination = signal(SHORT_PAGINATION);
  protected reload$ = new Subject<void>();

  protected type = computed(() =>
    this.employee() != null
      ? UnavailabilityType.EMPLOYEE
      : UnavailabilityType.DRIVER,
  );
  protected params = computed(() => ({
    past: this.showPastUnavailabilities(),
    ...this.pagination(),
  }));

  public ngOnInit(): void {
    this.reload$
      .pipe(
        tap(() => this.loading.set(true)),
        switchMap(() => {
          const request$ =
            this.type() === UnavailabilityType.EMPLOYEE
              ? this.employeesService.getEmployeeUnavailabilities(
                  this.employee()!.uuid,
                  this.params(),
                )
              : this.driversService.getDriverUnavailabilities(
                  this.driver()!.uuid,
                  this.params(),
                );

          return request$.pipe(
            catchError(() => {
              this.unavailabilities.set(null);
              this.error.set(true);
              this.loading.set(false);

              return EMPTY;
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((unavailabilities) => {
        this.unavailabilities.set(unavailabilities);
        this.loading.set(false);
        this.error.set(false);
      });

    effect(
      () => {
        this.params();
        untracked(() => this.reload$.next());
      },
      { injector: this.injector },
    );

    (this.type() === UnavailabilityType.EMPLOYEE
      ? this.employeesService.reload$
      : this.driversService.reload$
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.reload$.next());
  }

  protected addUnavailability(): void {
    if (this.type() === UnavailabilityType.EMPLOYEE) {
      this.openUnavailabilityModal({
        mode: OnyxFormMode.ADD,
        type: UnavailabilityType.EMPLOYEE,
        item: this.employee()!,
      });
    } else {
      this.openUnavailabilityModal({
        mode: OnyxFormMode.ADD,
        type: UnavailabilityType.DRIVER,
        item: this.driver()!,
      });
    }
  }

  protected editUnavailability(unavailability: Unavailability): void {
    if (this.type() === UnavailabilityType.EMPLOYEE) {
      this.openUnavailabilityModal({
        mode: OnyxFormMode.EDIT,
        type: UnavailabilityType.EMPLOYEE,
        item: this.employee()!,
        unavailability,
      });
    } else {
      this.openUnavailabilityModal({
        mode: OnyxFormMode.EDIT,
        type: UnavailabilityType.DRIVER,
        item: this.driver()!,
        unavailability,
      });
    }
  }

  protected showFile(file: File | string): void {
    this.storageService.previewFile(file as string).subscribe();
  }

  private openUnavailabilityModal(data: UnavailabilityModalData): void {
    this.modalService.open<UnavailabilityModalData>(
      UnavailabilityModalComponent,
      data,
    );
  }
}
